import React, { useState, useRef } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, FileSpreadsheet } from 'lucide-react';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

/**
 * Multi-Tab File Upload Component
 * Handles uploading Excel files with multiple tabs to Google Cloud Storage and BigQuery
 */
export default function MultiTabFileUpload({
  fileType,
  schema,
  onUploadComplete,
  onUploadError
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');
  const [tabProgress, setTabProgress] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  /**
   * Handle file selection
   */
  const handleFileSelect = (file) => {
    if (!file) return;

    console.log(`📁 Multi-tab file selected for ${fileType}:`, file.name);
    validateAndUpload(file);
  };

  /**
   * Validate file and start upload
   */
  const validateAndUpload = async (file) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      setUploadStage('');

      // Basic validation
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        throw new Error(`Invalid file type. Please upload an Excel file (${validExtensions.join(', ')})`);
      }

      // Check file size
      const maxSizeBytes = (schema.maxSizeGB || 2) * 1024 * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        throw new Error(`File size exceeds ${schema.maxSizeGB}GB limit`);
      }

      setUploadStage('Validating file format...');
      setUploadProgress(10);

      // Initialize tab progress
      if (schema.tabs) {
        const initialProgress = {};
        Object.keys(schema.tabs).forEach(tabName => {
          initialProgress[tabName] = { status: 'pending', rows: 0 };
        });
        setTabProgress(initialProgress);
      }

      setUploadStage('Uploading to cloud storage...');
      setUploadProgress(30);

      // Upload to Cloud Storage
      const storageUrl = await uploadToStorage(file, fileType);

      setUploadProgress(60);
      setUploadStage('Processing tabs...');

      // The backend will handle tab extraction and BigQuery loading
      const result = {
        fileName: file.name,
        uploadTime: new Date().toISOString(),
        fileSize: file.size,
        storageUrl: storageUrl,
        status: 'processing',
        tabCount: schema.tabs ? Object.keys(schema.tabs).length : 0
      };

      setUploadProgress(100);
      setUploadStage('Upload complete!');

      console.log(`✅ Multi-tab upload completed for ${fileType}:`, result);
      onUploadComplete(result);

      // Reset after success
      setTimeout(() => {
        setUploadProgress(0);
        setUploadStage('');
        setTabProgress({});
      }, 3000);

    } catch (error) {
      console.error(`❌ Multi-tab upload failed for ${fileType}:`, error);
      setUploadStage(`Error: ${error.message}`);
      onUploadError(error);

      // Don't auto-reset - let user dismiss the error manually
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  /**
   * Upload file to Google Cloud Storage
   */
  const uploadToStorage = async (file, fileType) => {
    try {
      const storage = getStorage();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `${fileType}/${timestamp}_${file.name}`;
      const storageRef = ref(storage, `inmocean-raw-data/${filename}`);

      console.log(`📤 Uploading to storage: ${filename}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      console.log(`✅ File uploaded to storage: ${downloadURL}`);
      return downloadURL;

    } catch (error) {
      console.error('❌ Storage upload failed:', error);
      throw new Error(`Storage upload failed: ${error.message}`);
    }
  };

  /**
   * Handle drag and drop
   */
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  /**
   * Handle click to select file
   */
  const handleClick = () => {
    if (!uploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const tabCount = schema.tabs ? Object.keys(schema.tabs).length : 0;

  return (
    <div className="relative w-full">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={(e) => handleFileSelect(e.target.files[0])}
        className="hidden"
      />

      {/* Drag and Drop Area */}
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative flex items-center justify-between gap-3 px-4 py-3
          border rounded-lg transition-all cursor-pointer
          ${uploading
            ? 'bg-slate-50 border-slate-300 cursor-not-allowed'
            : dragOver
            ? 'bg-blue-50 border-blue-300 shadow-sm'
            : 'bg-white border-slate-200 hover:border-blue-300 hover:bg-blue-50/30'
          }
        `}
      >
        <div className="flex items-center gap-3 flex-1">
          {uploading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-400 border-t-transparent"></div>
              <div>
                <p className="text-sm font-medium text-slate-700">Uploading...</p>
                <p className="text-xs text-slate-500">{uploadProgress}% • {uploadStage}</p>
              </div>
            </>
          ) : uploadProgress === 100 ? (
            <>
              <CheckCircle size={20} className="text-emerald-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-emerald-700">Complete</p>
                <p className="text-xs text-emerald-600">{tabCount} tabs uploaded</p>
              </div>
            </>
          ) : uploadStage && uploadStage.includes('Error') ? (
            <>
              <AlertCircle size={20} className="text-rose-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-rose-700">Upload Failed</p>
                <p className="text-xs text-rose-600 break-words">
                  {uploadStage.replace('Error: ', '').substring(0, 60)}...
                </p>
              </div>
            </>
          ) : (
            <>
              <FileSpreadsheet size={18} className={dragOver ? 'text-blue-600' : 'text-slate-400'} />
              <div>
                <p className={`text-sm font-medium ${dragOver ? 'text-blue-700' : 'text-slate-700'}`}>
                  {dragOver ? 'Drop file to upload' : 'Drag & drop or click'}
                </p>
                <p className="text-xs text-slate-500">
                  Excel with {tabCount} tabs • Max {schema.maxSizeGB}GB
                </p>
              </div>
            </>
          )}
        </div>

        {!uploading && uploadProgress !== 100 && (
          <div className="text-xs text-blue-600 font-medium whitespace-nowrap">
            Browse
          </div>
        )}
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg p-3 shadow-lg z-10">
          <div className="space-y-2">
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>

            {/* Progress Text */}
            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-600">{uploadStage}</span>
              <span className="font-medium text-gray-900">{uploadProgress}%</span>
            </div>

            {/* Tab Progress */}
            {Object.keys(tabProgress).length > 0 && (
              <div className="space-y-1 pt-2 border-t">
                <p className="text-xs font-medium text-slate-600">Tabs:</p>
                {Object.entries(tabProgress).map(([tabName, progress]) => (
                  <div key={tabName} className="flex items-center justify-between text-xs">
                    <span className="text-slate-600">{tabName}</span>
                    <span className={`
                      font-medium
                      ${progress.status === 'complete' ? 'text-emerald-600' : ''}
                      ${progress.status === 'error' ? 'text-rose-600' : ''}
                      ${progress.status === 'processing' ? 'text-blue-600' : ''}
                      ${progress.status === 'pending' ? 'text-slate-400' : ''}
                    `}>
                      {progress.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Success Message */}
      {uploadProgress === 100 && !uploadStage.includes('Error') && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-green-50 border border-green-200 rounded-lg p-3 shadow-lg z-10">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-green-800 text-sm">
              <CheckCircle size={16} />
              <span>Upload completed successfully!</span>
            </div>
            {schema.tabs && (
              <div className="text-xs text-green-700 pl-6">
                {Object.keys(schema.tabs).map(tabName => (
                  <div key={tabName}>• {tabName} → {schema.tabs[tabName].tableName}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Message Popup */}
      {!uploading && uploadStage && uploadStage.includes('Error') && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg z-10">
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={16} />
              <div className="flex-1">
                <p className="font-medium text-sm text-red-800">Upload Failed</p>
                <p className="text-sm text-red-700 mt-1 break-words">
                  {uploadStage.replace('Error: ', '')}
                </p>
              </div>
              <button
                onClick={() => setUploadStage('')}
                className="text-red-400 hover:text-red-600 text-sm font-medium"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drag overlay */}
      {dragOver && (
        <div className="absolute inset-0 bg-blue-50 border-2 border-blue-400 border-dashed rounded-lg flex items-center justify-center z-20">
          <div className="text-center">
            <FileSpreadsheet className="mx-auto text-blue-600 mb-2" size={32} />
            <p className="text-sm font-medium text-blue-700">Drop Excel file to upload</p>
            <p className="text-xs text-blue-600">
              {schema.name} • {tabCount} tabs • Max {schema.maxSizeGB}GB
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
